import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import { getMyTools, refreshMyTools, SelfServiceTool, ApiError } from '../lib/api-client';

interface FlatTool extends SelfServiceTool {
  resource_name: string;
}

type SensitivityFilter = 'all' | 'unclassified' | 'low' | 'medium' | 'high' | 'critical';

const summarizeRefresh = (resources: { status: string }[]): string => {
  const succeeded = resources.filter((r) => r.status === 'success').length;
  const failed = resources.length - succeeded;
  if (resources.length === 0) return 'No resources to refresh.';
  if (failed === 0) return `Refreshed ${succeeded} resource${succeeded === 1 ? '' : 's'}.`;
  return `Refreshed ${succeeded} of ${resources.length} resources — ${failed} unavailable right now.`;
};

export const MyTools = () => {
  const [rows, setRows] = useState<FlatTool[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [sensitivityFilter, setSensitivityFilter] = useState<SensitivityFilter>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [overflowing, setOverflowing] = useState<Set<string>>(new Set());
  const descRefs = useRef<Map<string, HTMLSpanElement>>(new Map());

  const loadTools = useCallback(() => {
    return getMyTools().then((response) => {
      const seen = new Set<string>();
      const flat: FlatTool[] = [];
      for (const agent of response.agents) {
        for (const resource of agent.resources) {
          for (const tool of resource.tools) {
            const key = `${resource.resource_name}:${tool.name}`;
            if (seen.has(key)) continue;
            seen.add(key);
            flat.push({ ...tool, resource_name: resource.resource_name });
          }
        }
      }
      setRows(flat);
    });
  }, []);

  useEffect(() => {
    let cancelled = false;

    loadTools().catch((err: ApiError) => {
      if (!cancelled) {
        setError(err.message || 'Failed to load your tools');
      }
    });

    return () => {
      cancelled = true;
    };
  }, [loadTools]);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setError(null);
    setRefreshMessage(null);
    try {
      const result = await refreshMyTools();
      setRefreshMessage(summarizeRefresh(result.resources));
      await loadTools();
    } catch (err) {
      setError((err as ApiError).message || 'Failed to refresh tools');
    } finally {
      setRefreshing(false);
    }
  }, [loadTools]);

  const resourceOptions = useMemo(() => {
    if (!rows) return [];
    return Array.from(new Set(rows.map((r) => r.resource_name))).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    if (!rows) return [];
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (resourceFilter && r.resource_name !== resourceFilter) return false;
      if (sensitivityFilter !== 'all') {
        const isUnclassified = !r.sensitivity;
        if (sensitivityFilter === 'unclassified' ? !isUnclassified : r.sensitivity !== sensitivityFilter) {
          return false;
        }
      }
      if (q && !r.name.toLowerCase().includes(q) && !(r.description || '').toLowerCase().includes(q)) {
        return false;
      }
      return true;
    });
  }, [rows, searchQuery, resourceFilter, sensitivityFilter]);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const registerDescRef = useCallback((key: string, el: HTMLSpanElement | null) => {
    if (el) {
      descRefs.current.set(key, el);
    } else {
      descRefs.current.delete(key);
    }
  }, []);

  // Text overflow depends on the column's actual rendered width (which is
  // responsive), not the description's character count — so this measures
  // real layout (scrollHeight vs the 2-line clamp's clientHeight) rather
  // than guessing from string length.
  const measureOverflow = useCallback(() => {
    const next = new Set<string>();
    descRefs.current.forEach((el, key) => {
      if (el.scrollHeight > el.clientHeight + 1) {
        next.add(key);
      }
    });
    setOverflowing(next);
  }, []);

  useEffect(() => {
    measureOverflow();
  }, [filteredRows, measureOverflow]);

  useEffect(() => {
    const handleResize = () => measureOverflow();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [measureOverflow]);

  return (
    <div>
      <div className="page-header-row">
        <div>
          <h1 className="page-title">My Tools</h1>
          <p className="page-subtitle">Tools available to you across your agents.</p>
        </div>
        <button
          type="button"
          className="refresh-tools-button"
          onClick={handleRefresh}
          disabled={refreshing || rows === null}
        >
          <RefreshCw size={14} className={refreshing ? 'spin' : undefined} />
          {refreshing ? 'Refreshing...' : 'Refresh Tools'}
        </button>
      </div>
      {refreshMessage && !error ? <p className="muted">{refreshMessage}</p> : null}

      {error ? (
        <p className="error">{error}</p>
      ) : rows === null ? (
        <div className="centered">
          <div className="spinner" />
          <p className="muted">Loading your tools...</p>
        </div>
      ) : (
        <>
          <div className="filter-card">
            <div className="filter-field search">
              <label htmlFor="tools-search">Search</label>
              <input
                id="tools-search"
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tool name or description"
              />
            </div>
            <div className="filter-field select">
              <label htmlFor="tools-resource">Resource</label>
              <select
                id="tools-resource"
                value={resourceFilter}
                onChange={(e) => setResourceFilter(e.target.value)}
              >
                <option value="">All resources</option>
                {resourceOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </select>
            </div>
            <div className="filter-field select">
              <label htmlFor="tools-sensitivity">Sensitivity</label>
              <select
                id="tools-sensitivity"
                value={sensitivityFilter}
                onChange={(e) => setSensitivityFilter(e.target.value as SensitivityFilter)}
              >
                <option value="all">All</option>
                <option value="unclassified">Unclassified</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {filteredRows.length === 0 ? (
            <p className="muted">No tools match these filters.</p>
          ) : (
            <div className="table-scroll">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Tool</th>
                    <th>Sensitivity</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredRows.map((tool) => {
                    const key = `${tool.resource_name}:${tool.name}`;
                    const isExpanded = expanded.has(key);
                    const canExpand = overflowing.has(key);
                    const sensitivity = tool.sensitivity || 'unclassified';
                    return (
                      <tr
                        key={key}
                        className={canExpand ? 'clickable' : undefined}
                        onClick={canExpand ? () => toggleExpanded(key) : undefined}
                      >
                        <td>{tool.resource_name}</td>
                        <td>
                          <code>{tool.name}</code>
                        </td>
                        <td>
                          <span className={`sensitivity-badge ${sensitivity}`}>{sensitivity}</span>
                        </td>
                        <td className={`description-cell${isExpanded ? ' expanded' : ''}`}>
                          {tool.description ? (
                            <>
                              <span
                                className="description-text"
                                ref={(el) => registerDescRef(key, el)}
                              >
                                {tool.description}
                              </span>
                              {canExpand ? (
                                <div className="expand-hint">
                                  {isExpanded ? (
                                    <>
                                      <ChevronUp size={12} /> Show less
                                    </>
                                  ) : (
                                    <>
                                      <ChevronDown size={12} /> Show more
                                    </>
                                  )}
                                </div>
                              ) : null}
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};
